import React from "react";
import { BootstrapDialog } from "./styles";
import {
  Box,
  Chip,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import { colors } from "../../../utility/types/constant";
import CloseIcon from "@mui/icons-material/Close";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import {
  WEEK_DAYS,
  formatContribution,
  formatPayOutDay,
  getCircleCountryInfo,
  getCurrencyCode,
  getFrequency,
  getSizeLabel,
  normalizeDays,
} from "../../circle/circleDisplay";

interface CircleLimitedDetailsProps {
  open: boolean;
  handleClose: () => void;
  title?: string;
  sx?: any;
  circleData?: any;
}

const DetailTile = ({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: string;
}) => (
  <Box
    sx={{
      p: 1.75,
      borderRadius: "14px",
      bgcolor: alpha(colors.primary, 0.03),
      border: `1px solid ${alpha(colors.primary, 0.07)}`,
      minWidth: 0,
    }}
  >
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.secondary" }}>
      {icon}
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
    </Stack>
    <Typography
      sx={{ mt: 0.5, fontWeight: 800, fontSize: "1.05rem", color: colors.primary }}
    >
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" sx={{ color: "text.disabled" }}>
        {hint}
      </Typography>
    )}
  </Box>
);

const CircleLimitedDetails = (props: CircleLimitedDetailsProps) => {
  const { open, handleClose, title, sx, circleData } = props;

  const config = circleData?.configuration ?? {};
  const amount = formatContribution(config.contributionAmount, config.currency);
  const currencyCode = getCurrencyCode(config.currency);
  const frequency = getFrequency(config.contributionPeriod);
  const days = normalizeDays(config.contributionDays);
  const country = getCircleCountryInfo(config.country);
  const sizeLabel = getSizeLabel(circleData?.membersCount);
  const payOutDayLabel = formatPayOutDay(config.payOutDay);
  const iconSx = { fontSize: 16 };

  return (
    <React.Fragment>
      <BootstrapDialog
        open={open}
        onClose={handleClose}
        aria-labelledby="circle-details-title"
        sx={{
          ...sx,
          width: "100%",
          "& .MuiDialog-paper": { borderRadius: "20px", overflow: "hidden" },
        }}
        fullWidth
      >
        <Box
          sx={{
            position: "relative",
            px: 3,
            pt: 3,
            pb: 2.5,
            color: colors.white,
            background: `linear-gradient(135deg, ${colors.primary} 0%, #0a2159 70%, ${alpha(colors.mainSecondary, 0.65)} 140%)`,
          }}
        >
          <Typography
            id="circle-details-title"
            component="h2"
            sx={{
              pr: 6,
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 800,
              fontSize: "1.4rem",
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>
          {circleData?.description && (
            <Typography sx={{ mt: 1, color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>
              {circleData.description}
            </Typography>
          )}

          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 14,
              top: 14,
              color: colors.white,
              bgcolor: "rgba(255,255,255,0.12)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: "24px !important" }}>
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: "16px",
              bgcolor: alpha(colors.mainSecondary, 0.07),
              border: `1px solid ${alpha(colors.mainSecondary, 0.22)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                Each contribution
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={0.75}>
                <Typography
                  sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 800,
                    fontSize: "1.9rem",
                    lineHeight: 1.1,
                    color: colors.primary,
                  }}
                >
                  {amount}
                </Typography>
                {!amount.includes(currencyCode) && (
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary" }}>
                    {currencyCode}
                  </Typography>
                )}
              </Stack>
            </Box>
            <Chip
              icon={<EventRepeatRoundedIcon />}
              label={frequency.detail ? `${frequency.label} · ${frequency.detail}` : frequency.label}
              sx={{
                fontWeight: 700,
                color: colors.white,
                bgcolor: colors.primary,
                "& .MuiChip-icon": { color: colors.white },
              }}
            />
          </Box>

          <Typography variant="caption" component="div" sx={{ mb: 0.75, color: "text.secondary", fontWeight: 600 }}>
            Contribution days
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ mb: 2.5 }}
            role="img"
            aria-label={days.length ? `Contributes on ${days.join(", ")}` : "No contribution days set"}
          >
            {WEEK_DAYS.map((day) => {
              const on = days.includes(day);
              return (
                <Box
                  key={day}
                  aria-hidden
                  sx={{
                    flex: 1,
                    py: 0.9,
                    borderRadius: "10px",
                    textAlign: "center",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: on ? colors.white : alpha(colors.primary, 0.35),
                    bgcolor: on ? colors.mainSecondary : alpha(colors.primary, 0.04),
                    border: `1px solid ${on ? colors.mainSecondary : alpha(colors.primary, 0.08)}`,
                  }}
                >
                  {day.slice(0, 3)}
                </Box>
              );
            })}
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
              gap: 1.25,
            }}
          >
            <DetailTile
              icon={<GroupsRoundedIcon sx={iconSx} />}
              label="Members"
              value={circleData?.members?.length ?? 0}
              hint={sizeLabel ? `Size: ${sizeLabel}` : undefined}
            />
            <DetailTile
              icon={<PaymentsRoundedIcon sx={iconSx} />}
              label="Payout"
              value={config.payOutPeriod || "—"}
              hint={payOutDayLabel || undefined}
            />
            <DetailTile
              icon={<SavingsRoundedIcon sx={iconSx} />}
              label="Currency"
              value={currencyCode}
            />
            <DetailTile
              icon={<PublicRoundedIcon sx={iconSx} />}
              label="Country"
              value={country ? `${country.flag} ${country.code}` : "—"}
              hint={country?.name}
            />
          </Box>
        </DialogContent>
      </BootstrapDialog>
    </React.Fragment>
  );
};

export default CircleLimitedDetails;
